import React, { useRef, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Button, TextField, Typography,
  Alert, CircularProgress, Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from '../utils/axios';

/**
 * "SEO & Sharing" fields shared by the Category and Sub-category modals.
 *
 * value:    { image, title, metaDescription, content }
 * onChange: (partial) => void
 *
 * Storefront rules (see Teaming_Frontend_2.0 category/sub-category pages):
 *  - image            → og:image / share card (falls back to the site default)
 *  - title / meta     → used as written on /all-india pages; on city pages only
 *                       when they contain {city} (replaced per city), otherwise
 *                       the storefront generates a city-specific version so 100+
 *                       city pages never share one title/description.
 *  - content          → the SEO text block shown on the page (HTML).
 */
const count = (s) => (s || '').length;

const CatalogSeoFields = ({ value, onChange, entityLabel = 'category' }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setUploadError('Please choose an image file (JPG, PNG or WebP).');
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('seo_image', file);
      const res = await axios.post('/admin/categories/seo-image', fd, { skipNotify: 'success' });
      if (res.data?.url) onChange({ image: res.data.url });
    } catch (err) {
      setUploadError(err.response?.data?.msg || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const titleLen = count(value.title);
  const descLen = count(value.metaDescription);

  return (
    <Accordion disableGutters variant="outlined" sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>SEO &amp; Sharing</Typography>
          <Typography variant="caption" color="text.secondary">
            Share image, Google title, meta description and page content for this {entityLabel}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2.5}>
          {/* Share image */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Share image</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Shown when pages are shared on WhatsApp, Facebook, LinkedIn and in Google / AI results.
              Uploads are resized to 1200×630 automatically. Use your own image — never link images from other websites.
            </Typography>
            {value.image ? (
              <Box
                component="img"
                src={value.image}
                alt="Share preview"
                sx={{ width: '100%', maxWidth: 360, aspectRatio: '1200 / 630', objectFit: 'cover', borderRadius: 1.5, border: 1, borderColor: 'divider', display: 'block', mb: 1 }}
              />
            ) : (
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                No image set — the site's default share image will be used.
              </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} />
              <Button
                variant="outlined"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={14} /> : null}
              >
                {uploading ? 'Uploading…' : value.image ? 'Replace image' : 'Upload image'}
              </Button>
              {value.image && (
                <Button color="error" onClick={() => onChange({ image: '' })} disabled={uploading}>
                  Remove
                </Button>
              )}
            </Stack>
            {uploadError && <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>}
            <TextField
              label="…or image URL"
              value={value.image || ''}
              onChange={(e) => onChange({ image: e.target.value.trim() })}
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              placeholder="https://…"
            />
          </Box>

          {/* SEO title */}
          <TextField
            label="SEO title (optional)"
            value={value.title || ''}
            onChange={(e) => onChange({ title: e.target.value })}
            fullWidth
            placeholder={`e.g. Fashion Wholesalers & Suppliers in {city}`}
            helperText={`${titleLen}/60 · Add {city} to use it on every city page (e.g. "… in {city}"). Without {city} it is only used on the All-India page. Leave empty to auto-generate.`}
            error={titleLen > 70}
          />

          {/* Meta description */}
          <TextField
            label="Meta description (optional)"
            value={value.metaDescription || ''}
            onChange={(e) => onChange({ metaDescription: e.target.value })}
            fullWidth
            multiline
            minRows={2}
            helperText={`${descLen}/155 · Same {city} rule as the title. Leave empty to auto-generate a city-specific description.`}
            error={descLen > 170}
          />

          {/* SEO content */}
          <TextField
            label="Page content (optional, HTML)"
            value={value.content || ''}
            onChange={(e) => onChange({ content: e.target.value })}
            fullWidth
            multiline
            minRows={6}
            helperText={`Shown in the "About this ${entityLabel}" section. Allowed: <h2>, <h3>, <p>, <ul>, <li>, <a>, <strong>. Make sure it is about THIS ${entityLabel}.`}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default CatalogSeoFields;
